import { alpha, lighten } from '@mui/material/styles';
import {
  colors,
  extendedColors,
  fontStacks,
  materialColors,
  translucentColors,
} from './primitives';

export const colorTokens = {
  content: {
    primary: colors.black,
    secondary: colors.gray2,
    tertiary: colors.gray4,
    muted: colors.gray12,
    disabled: colors.gray11,
    placeholder: colors.gray7,
    subtle: colors.slate,
    faint: colors.gray14,
    inverse: colors.white,
    inverseMuted: colors.gray10,
    translucent: {
      primary: translucentColors.black87,
      secondary: translucentColors.black60,
      disabled: translucentColors.black38,
    },
  },
  surface: {
    base: colors.white,
    subtle: colors.gray3,
    muted: colors.gray10,
    tinted: colors.gray9,
    hover: colors.gray8,
    strong: colors.gray5,
    soft: colors.gray15,
    sunken: colors.gray6,
    emphasis: colors.gray1,
    control: colors.gray14,
    track: colors.gray,
    trackStrong: colors.gray4,
    light: colors.gray13,
    divider: colors.gray16,
    raised: colors.gray17,
    inverse: colors.black,
    inverseMuted: colors.gray2,
    selected: colors.lightBlue3,
    selectedSubtle: colors.lightBlue6,
    highlight: colors.lightBlue4,
    active: colors.lightBlue5,
    shade: extendedColors.neutral235,
    shadeLight: extendedColors.neutral240,
    fade: extendedColors.neutral254,
    infoSubtle: extendedColors.lightBlueAlice,
    scrollbar: extendedColors.scrollbarThumb,
    chrome: materialColors.grey[100],
    chromeHover: materialColors.grey[300],
    backdrop: translucentColors.ink50,
    translucent: {
      hover: translucentColors.black04,
      selected: translucentColors.black08,
      focus: translucentColors.black12,
      disabled: translucentColors.black12,
      pressed: colors.blackOpacity5,
      scrim: translucentColors.black40,
      scrimStrong: translucentColors.black50,
      frosted: translucentColors.white90,
      frostedLight: translucentColors.white60,
      accent: translucentColors.aliceBlueAt90,
      frostedGray: translucentColors.gray221At34,
      subtle: translucentColors.black05,
    },
  },
  border: {
    default: colors.gray5,
    subtle: colors.gray8,
    light: colors.gray13,
    muted: colors.gray,
    divider: colors.gray16,
    strong: colors.black,
    control: colors.gray7,
    controlStrong: colors.gray1,
    emphasis: colors.gray17,
    medium: colors.gray18,
    faint: colors.gray14,
    selected: colors.lightBlue3,
    contrast: colors.gray2,
    info: colors.lightBlue2,
    highlight: colors.lightBlue4,
    tinted: colors.gray9,
    dividerSubtle: colors.gray3,
    disabled: colors.gray10,
    inverse: colors.white,
    mediumSoft: colors.gray11,
    mediumStrong: colors.gray4,
    translucent: translucentColors.black12,
  },
  shadow: {
    faint: translucentColors.black03,
    light: translucentColors.black04,
    subtle: translucentColors.black05,
    soft: translucentColors.black08,
    medium: translucentColors.black10,
    strong: translucentColors.black12,
    heavy: translucentColors.black25,
    inkSubtle: colors.blackOpacity5,
    solid: colors.gray11,
    solidSoft: colors.gray14,
    solidMuted: colors.gray17,
    solidLight: colors.gray13,
  },
  icon: {
    primary: colors.black,
    secondary: colors.gray2,
    tertiary: colors.gray4,
    muted: colors.gray12,
    disabled: colors.gray11,
    subtle: colors.slate,
    faint: colors.gray14,
    inverse: colors.white,
    translucent: {
      active: translucentColors.black54,
      disabled: translucentColors.black26,
    },
  },
  status: {
    error: {
      main: colors.red2,
      bright: colors.red,
      soft: colors.red100,
      bg: colors.red10,
      bgStrong: colors.lightRed,
      bgSoft: colors.lightRed2,
      fill: alpha(colors.red, 0.2),
      fillSoft: alpha(colors.red100, 0.2),
    },
    warning: {
      main: colors.orange,
      strong: colors.orange100,
      accent: colors.orange2,
      light: lighten(colors.orange, 0.2),
      bg: colors.orange10,
      bgStrong: colors.lightOrange,
      bgSoft: colors.lightOrange2,
      fill: alpha(colors.orange, 0.2),
      fillStrong: alpha(colors.orange100, 0.2),
    },
    success: {
      main: colors.green100,
      bright: colors.green,
      light: lighten(colors.green100, 0.2),
      bg: colors.green10,
      bgStrong: colors.green200,
      bgSoft: colors.lightTurqoise2,
      bgBright: colors.lightTurqoise,
      fill: alpha(colors.green, 0.2),
      fillStrong: alpha(colors.green100, 0.2),
    },
    info: {
      main: colors.blue,
      bg: colors.gray9,
      bgStrong: colors.lightBlue,
    },
    neutral: {
      fill: translucentColors.black20,
    },
    attention: {
      bg: colors.attentionYellow,
    },
  },
  accent: {
    primary: colors.blue4,
    primaryStrong: colors.blue6,
    secondary: colors.blue7,
    secondaryFaint: alpha(colors.blue7, 0.1),
    link: colors.blue,
    selected: colors.blue5,
    ai: colors.magenta,
    aiSubtle: colors.lightMagenta,
    aiFaint: colors.magentaOpacity5,
    aiTint: colors.lightPink,
    aiGradient: {
      start: extendedColors.aiGradientPink,
      middle: extendedColors.aiGradientRose,
      end: extendedColors.aiGradientBlue,
      startFaint: alpha(extendedColors.aiGradientPink, 0.33),
      endFaint: alpha(extendedColors.aiGradientBlue, 0.33),
    },
    secondaryMuted: colors.blue2,
    tertiary: colors.purple,
    tertiarySubtle: colors.lightPurple,
  },
  chart: {
    grid: colors.gray16,
    gridLine: colors.gray,
    idle: colors.gray4,
    marker: colors.black,
    segment: colors.gray12,
    segmentTrack: colors.gray5,
    other: extendedColors.slateGray,
    expectedArea: extendedColors.neutral238,
    delayArea: extendedColors.neutral248,
    axis: colors.gray18,
    neutral: colors.gray1,
    muted: colors.gray11,
    inactive: colors.jetGrey,
    highlight: colors.yellow,
    band: alpha(colors.gray18, 0.2),
    bandStrong: translucentColors.gray161At40,
    series: {
      plum: extendedColors.seriesPlum,
      rose: extendedColors.seriesRose,
      red: extendedColors.seriesRed,
      orange: extendedColors.seriesOrange,
      teal: extendedColors.seriesTeal,
      turquoise: extendedColors.seriesTurquoise,
      aqua: extendedColors.seriesAqua,
      green: extendedColors.seriesGreen,
      lime: extendedColors.seriesLime,
      yellow: extendedColors.seriesYellow,
      blue: extendedColors.seriesBlue,
      sky: extendedColors.seriesSky,
      navy: extendedColors.seriesNavy,
      violet: extendedColors.seriesViolet,
      lavender: extendedColors.seriesLavender,
    },
    seriesFill: {
      plum: alpha(extendedColors.seriesPlum, 0.2),
      rose: alpha(extendedColors.seriesRose, 0.2),
      red: alpha(extendedColors.seriesRed, 0.2),
      orange: alpha(extendedColors.seriesOrange, 0.2),
      teal: alpha(extendedColors.seriesTeal, 0.2),
      turquoise: alpha(extendedColors.seriesTurquoise, 0.2),
      aqua: alpha(extendedColors.seriesAqua, 0.2),
      green: alpha(extendedColors.seriesGreen, 0.2),
      lime: alpha(extendedColors.seriesLime, 0.2),
      yellow: alpha(extendedColors.seriesYellow, 0.2),
      blue: alpha(extendedColors.seriesBlue, 0.2),
      sky: alpha(extendedColors.seriesSky, 0.2),
      navy: alpha(extendedColors.seriesNavy, 0.2),
      violet: alpha(extendedColors.seriesViolet, 0.2),
      lavender: alpha(extendedColors.seriesLavender, 0.2),
    },
  },
  identity: {
    avatar: {
      purple: extendedColors.avatarPurple,
      red: extendedColors.avatarRed,
      blue: extendedColors.avatarBlue,
      green: extendedColors.avatarGreen,
      yellow: extendedColors.avatarYellow,
      orange: extendedColors.avatarOrange,
      magenta: extendedColors.avatarMagenta,
    },
    github: extendedColors.githubInk,
    gitlab: extendedColors.gitlabOrange,
    bitbucket: extendedColors.bitbucketBlue,
    azure: extendedColors.azureBlue,
    azureFaint: alpha(extendedColors.azureBlue, 0.12),
    avatarFallback: extendedColors.pureBlack,
  },
  code: {
    editorBg: extendedColors.editorBg,
    gutterBg: extendedColors.editorGutterBg,
    gutterText: extendedColors.editorGutterText,
    selection: translucentColors.gray200At30,
    diffAddedBg: colors.lightGreen,
    diffRemovedBg: colors.lightRed,
  },
  neutral: materialColors.grey,
};

const shadowColor = colorTokens.shadow;

export const scaleTokens = {
  space: {
    hairline: '1px',
    xxs: '2px',
    xxsPlus: '3px',
    xs: '4px',
    xsPlus: '5px',
    smMinus: '6px',
    sm: '8px',
    smPlus: '9px',
    mdMinus: '10px',
    md: '12px',
    mdPlus: '14px',
    lg: '16px',
    lgPlus: '18px',
    xl: '20px',
    xxl: '24px',
    xxxl: '32px',
    hugeMinus: '38px',
    huge: '40px',
    massive: '48px',
  },
  radius: {
    xxs: '2px',
    xs: '3px',
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '12px',
    xxl: '16px',
    capsule: '100px',
    pill: '999px',
    round: '50%',
  },
  fontFamily: {
    sans: fontStacks.geist,
    sansFace: fontStacks.geistFace,
    mono: fontStacks.geistMono,
    monoSystem: 'monospace',
    code: "'SFMono-Regular', 'Menlo', 'Consolas', 'Liberation Mono', 'Courier New', monospace",
    display: 'DM Sans',
  },
  fontSize: {
    xxxs: '9px',
    xxs: '10px',
    xs: '11px',
    sm: '12px',
    md: '13px',
    base: '14px',
    lg: '16px',
    xl: '18px',
    xxl: '20px',
    xxxl: '24px',
    display: '28px',
    displayLg: '34px',
    displayXl: '40px',
  },
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    none: '1',
    snug: '1.2',
    tight: '1.3',
    comfortable: '1.4',
    relaxed: '1.5',
    loose: '1.6',
    sm: '18px',
    md: '20px',
    display: '44px',
  },
  shadow: {
    xs: `0 1px 3px ${shadowColor.medium}`,
    sm: `0 2px 6px ${shadowColor.subtle}`,
    smSoft: `0 2px 6px ${shadowColor.soft}`,
    smMedium: `0 2px 6px ${shadowColor.medium}`,
    md: `0px 4px 8px ${shadowColor.medium}`,
    mdInk: `0 4px 8px 0 ${alpha(colors.black, 0.1)}`,
    mdTight: `0 2px 8px ${shadowColor.medium}`,
    lg: `0 4px 16px ${shadowColor.strong}`,
    sheet: `0px -6px 15px -5px ${shadowColor.faint}`,
    float: `0px 16px 250px 0px ${shadowColor.subtle}`,
    glow: `0px 0px 5px 0px ${shadowColor.heavy}`,
    halo: `0px 0px 18px 0px ${translucentColors.inkBlueAt13}`,
    haze: `0 0 11.5px 0 ${translucentColors.lavenderAt50}`,
  },
};
